import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

type TocItem = {
  id: string;
  label: string;
};

type Props = {
  items: TocItem[];
};

/**
 * 記事内目次（開閉式）
 * Accordion ではなく Collapse で実装し、展開時のマージン問題を回避する。
 */
const WorkflowTableOfContents: React.FC<Props> = ({ items }) => {
  const [open, setOpen] = React.useState(true);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* ヘッダー */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setOpen((v) => !v)}
        sx={{
          px: 2,
          py: 1,
          cursor: 'pointer',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.02)',
          userSelect: 'none',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <ListAltOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            目次
          </Typography>
        </Stack>
        <IconButton size="small" sx={{ p: 0.25 }}>
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              color: 'text.secondary',
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </IconButton>
      </Stack>

      {/* コンテンツ */}
      <Collapse in={open}>
        <Stack spacing={0} sx={{ px: 2, pt: 1, pb: 1.5 }}>
          {items.map((item, idx) => (
            <Link
              key={item.id}
              href={`#${item.id}`}
              underline="none"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                px: 1,
                borderRadius: 1,
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(25, 118, 210, 0.15)'
                      : 'rgba(25, 118, 210, 0.08)',
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.65rem' }}
                >
                  {idx + 1}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                {item.label}
              </Typography>
            </Link>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default WorkflowTableOfContents;
